import React, { useState } from "react";
import { BadgeCheck, ChevronDown, Info, Percent, ShieldCheck, Wallet } from "lucide-react";
import { useI18n } from "../i18n";
import { formatMoney } from "../lib/format";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

/** Human sentence for a single refund rule (mode aware). */
export const ruleSentence = (rule, t) => {
  if (!rule) return "";
  if (rule.mode === "gross_percent") return t("employee.ruleGrossShort", { pct: rule.gross_percent });
  if (rule.mode === "both")
    return t("employee.ruleBothShort", { vat: rule.vat_percent, gross: rule.gross_percent });
  return t("employee.ruleVatShort", { pct: rule.vat_percent });
};

const exampleRefund = (rule, gross, vat) => {
  if (!rule) return 0;
  let amount = 0;
  if (rule.mode === "vat_percent" || rule.mode === "both") amount += (vat * (rule.vat_percent || 0)) / 100;
  if (rule.mode === "gross_percent" || rule.mode === "both")
    amount += (gross * (rule.gross_percent || 0)) / 100;
  if (rule.max_per_receipt != null) amount = Math.min(amount, rule.max_per_receipt);
  return Math.round(amount * 100) / 100;
};

/**
 * Read-only card showing the refund rule that applies to the signed-in worker,
 * including any personal override the admin configured for them.
 */
export const RefundRuleCard = ({ rule, currency = "EUR", compact = false }) => {
  const { t } = useI18n();
  const [showAll, setShowAll] = useState(false);
  if (!rule?.effective) return null;

  const eff = rule.effective;
  const special = rule.special_categories || [];
  const list = showAll ? rule.by_category || [] : special;

  return (
    <div className="ll-card p-4" data-testid="employee-my-rule-card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Wallet className="h-4 w-4 text-primary" />
          {t("employee.myRuleTitle")}
        </div>
        <Badge
          variant={rule.has_override ? "default" : "secondary"}
          className="gap-1"
          data-testid="employee-my-rule-source"
        >
          {rule.has_override ? <BadgeCheck className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
          {rule.has_override ? t("employee.myRuleCustom") : t("employee.myRuleCompany")}
        </Badge>
      </div>

      <p className="mt-1 text-xs text-muted-foreground">
        {rule.has_override ? t("employee.myRuleCustomNote") : t("employee.myRuleCompanyNote")}
      </p>

      <div className="mt-3 rounded-xl border bg-muted/30 p-3">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
          <Percent className="h-3 w-3" /> {t("employee.ruleBase")}
        </div>
        <div
          className="ll-num mt-0.5 font-display text-lg font-semibold text-primary"
          data-testid="employee-my-rule-base"
        >
          {ruleSentence(eff, t)}
        </div>
        <p className="mt-1 text-xs text-muted-foreground" data-testid="employee-my-rule-cap">
          {eff.max_per_receipt != null
            ? t("employee.ruleCap", { amount: formatMoney(eff.max_per_receipt, currency) })
            : t("employee.ruleNoCap")}
        </p>
      </div>

      {!compact ? (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="employee-my-rule-example">
          {t("employee.ruleExample", {
            gross: formatMoney(100, currency),
            vat: formatMoney(20, currency),
            refund: formatMoney(exampleRefund(eff, 100, 20), currency),
          })}
        </p>
      ) : null}

      {rule.note ? (
        <div className="mt-3 rounded-lg border border-primary/25 bg-primary/5 p-2.5">
          <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-primary">
            <Info className="h-3 w-3" /> {t("employee.ruleAdminNote")}
          </div>
          <p className="mt-0.5 text-xs text-foreground" data-testid="employee-my-rule-note">
            {rule.note}
          </p>
        </div>
      ) : null}

      {(rule.by_category || []).length ? (
        <div className="mt-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("employee.ruleByCategory")}
            </h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-xs"
              onClick={() => setShowAll((v) => !v)}
              data-testid="employee-my-rule-toggle-categories"
            >
              {showAll ? t("employee.ruleHideAll") : t("employee.ruleShowAll")}
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-200 ${showAll ? "rotate-180" : ""}`}
              />
            </Button>
          </div>

          {special.length && !showAll ? (
            <p className="mt-1 text-xs text-muted-foreground">{t("employee.ruleSpecialHint")}</p>
          ) : null}

          {list.length ? (
            <ul className="mt-2 divide-y rounded-lg border" data-testid="employee-my-rule-category-list">
              {list.map((c) => (
                <li key={c.key} className="flex items-center justify-between gap-3 px-3 py-2">
                  <span className="flex min-w-0 items-center gap-2 text-sm">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: c.color }}
                      aria-hidden="true"
                    />
                    <span className="truncate">{c.label}</span>
                  </span>
                  <span className="ll-num shrink-0 text-xs font-medium text-muted-foreground">
                    {ruleSentence(c, t)}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <p className="mt-3 flex items-start gap-1.5 text-xs text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {rule.require_approval ? t("employee.ruleApproval") : t("employee.ruleNoApproval")}
      </p>
    </div>
  );
};

export default RefundRuleCard;
